import RolodexApi from '@/api-interface/rolordex'
import { isEmpty, orderBy } from 'lodash-es'

async function getContact(contactKey) {
  let contact = null
  try {
    contact = await RolodexApi.getContactByKey({ companyRolodexContactKey: contactKey })
  } catch (error) {
    // because the contacts API has  tendency to be flaky... swallow the error so it doesn't cause everything to fail
    console.error(`Error fetching contact info [${contactKey}]`, error)
  }
  return contact
}

/**
 * Takes the "raw" CompanyJob object from the server and re-maps everything in to sensible bits that can be used by the UI.
 * Everything gets put in to the "transaction" object. You can think of this as the opposite of the "transactionPayload" computed property
 * @param savedData
 * @returns {Promise<void>}
 */
export default async function(savedData) {
  const transaction = {
    companyJobCarrierCertifiedAccurateFlag: savedData.companyJobCarrierCertifiedAccurateFlag,
    companyJobStatus: 'draft',
    companyJobKey: '',
    createdByAccountKey: null,
    basicInfo: {
      companyJobId: null,
      rate: null,
      milestones: {},
      documents: []
    },
    origins: [],
    destinations: [],
    freight: [],
    equipment: {},
    companyJobPointFreightMapping: []
  }

  transaction.companyJobKey = savedData.companyJobKey
  transaction.basicInfo.companyJobId = savedData.companyJobId
  transaction.basicInfo.rate = savedData.companyJobRate
  transaction.basicInfo.documents = savedData.companyJobDocuments || []
  transaction.companyJobStatus = savedData.companyJobStatus
  transaction.createdByAccountKey = savedData.createdByAccountKey
  // restore equipment information
  transaction.equipment = {
    companyJobTrailerType: savedData.companyJobTrailerType,
    companyJobStrapCount: savedData.companyJobStrapCount,
    companyJobInsuranceCoverageMinimum: savedData.companyJobInsuranceCoverageMinimum,
    companyJobEquipmentComments: savedData.companyJobEquipmentComments
  }

  // sort all jobPoints based on the earliest arrival date
  savedData.companyJobPoints = orderBy(savedData.companyJobPoints, 'companyJobPointDateEarliest', 'asc')

  // restore origin and destination info
  const formattedJobPointsPromise = savedData.companyJobPoints.map(async (point) => {
    // let companyJobPointDate = point.companyJobPointDateEarliest

    // guard against null values by setting some defaults
    const jobLocation = point.companyRolodexJobLocation || {
      companyRolodexJobLocationAddresses: [{}]
    }

    /*
    if (point.CompanyRolodexContactKey1) {
      point.CompanyRolodexContactKey1 = await RolodexApi.getContactByKey({companyRolodexContactKey: point.CompanyRolodexContactKey1})
    }

    if (point.CompanyRolodexContactKey2) {
      point.CompanyRolodexContactKey2 = await RolodexApi.getContactByKey({companyRolodexContactKey: point.CompanyRolodexContactKey2})
    }
    */

    return {
      ...point,
      CompanyRolodexContactKey1: point.CompanyRolodexContactKey1
        ? await getContact(point.CompanyRolodexContactKey1)
        : null,
      CompanyRolodexContactKey2: point.CompanyRolodexContactKey2
        ? await getContact(point.CompanyRolodexContactKey2)
        : null,
      address: {
        ...jobLocation.companyRolodexJobLocationAddresses[0],
        facilityName: jobLocation.companyRolodexJobLocationName,
        street1: jobLocation.companyRolodexJobLocationAddresses[0].companyRolodexJobLocationAddressStreet1,
        street2: jobLocation.companyRolodexJobLocationAddresses[0].companyRolodexJobLocationAddressStreet2,
        zip: jobLocation.companyRolodexJobLocationAddresses[0].companyRolodexJobLocationAddressZip,
        state: jobLocation.companyRolodexJobLocationAddresses[0].companyRolodexJobLocationAddressState,
        city: jobLocation.companyRolodexJobLocationAddresses[0].companyRolodexJobLocationAddressCity
      }
    }
  })

  const formattedJobPoints = await Promise.all(formattedJobPointsPromise)
  transaction.origins = formattedJobPoints.filter((point) => point.companyJobPointType === 'origin')

  transaction.destinations = formattedJobPoints.filter((point) => point.companyJobPointType === 'destination')

  // restore freight information
  transaction.freight = savedData.companyJobFreights.map((freight) => {
    const cleanFreight = Object.assign({}, freight)

    // from the mapping array... pull out all points that belong to this specific freight (should be no more than 2)
    const jobPointKeys = savedData.companyJobPointFreightMapping
      .filter((mapping) => mapping.companyJobFreightKey === freight.companyJobFreightKey)
      .map((mapping) => mapping.companyJobPointKey)

    // cross-reference those point keys and identify which ones are origin and which are destination
    const matchingOrigin = transaction.origins.find((origin) => jobPointKeys.includes(origin.companyJobPointKey))
    const matchingDestination = transaction.destinations.find((dest) => jobPointKeys.includes(dest.companyJobPointKey))
    if (!isEmpty(matchingOrigin)) {
      // TODO: clean this up
      cleanFreight.originKey = matchingOrigin.companyJobPointKey
      cleanFreight.origin = matchingOrigin.address
    } else {
      cleanFreight.originKey = null
    }
    if (!isEmpty(matchingDestination)) {
      // TODO: clean this up
      cleanFreight.destinationKey = matchingDestination.companyJobPointKey
      cleanFreight.destination = matchingDestination.address
    } else {
      cleanFreight.destinationKey = null
    }

    return cleanFreight
  })

  return transaction
}
